import { supabase } from '../lib/supabase_client';
import { devLog } from '../utils/devLogger';

/**
 * Get taxonomy structure from S3 via pre-signed URL
 * (AWS credentials are stored securely in Supabase Edge Function, not exposed in client)
 * 
 * @param orgId - Organization ID
 * @param filingId - Filing ID
 * @returns Taxonomy structure object
 */
export const getTaxonomyFromS3 = async (orgId: string, filingId: string): Promise<any> => {
  try {
    devLog(`📥 Getting pre-signed URL for taxonomy: ${orgId}/${filingId}`);
    
    // Step 1: Call Edge Function to get pre-signed URL (lightweight - just returns URL)
    const { data, error } = await supabase.functions.invoke('get-taxonomy', {
      body: {
        org_id: orgId,
        filing_id: filingId
      }
    });
    
    if (error) {
      throw new Error(error.message || 'Failed to get taxonomy URL');
    }
    
    if (!data || !data.taxonomy_url) {
      throw new Error('No taxonomy URL in response');
    }
    
    devLog(`✅ Got pre-signed URL (expires in ${data.expires_in}s)`);
    
    // Step 2: Fetch taxonomy JSON directly from S3 using pre-signed URL
    devLog(`📥 Fetching taxonomy from S3 directly...`);
    const taxonomyResponse = await fetch(data.taxonomy_url);
    
    if (!taxonomyResponse.ok) {
      throw new Error(`S3 fetch failed: ${taxonomyResponse.status} ${taxonomyResponse.statusText}`);
    }
    
    const taxonomy = await taxonomyResponse.json();
    devLog('✅ Taxonomy loaded from S3 (direct fetch)');
    
    return taxonomy;
  } catch (error) {
    console.error('❌ Error fetching taxonomy:', error);
    throw error;
  }
};
